import Image from 'next/image';

import { Container } from '@/atoms';

import { pickChild, pickChilds } from '@/utils';
import * as childs from './childs';

import type { FunctionComponent } from 'react';
import type { TypeFooterProps } from './types';

export const Footer: FunctionComponent<TypeFooterProps> = ({
  footerStyle,
  className = '',
  image,
  disclaimer,
  children,
}: TypeFooterProps) => {
  const [, columns] = pickChilds(children, childs.Column);
  const [, legal] = pickChild(children, childs.Legal);
  const [, socialMedia] = pickChild(children, childs.SocialMedia);

  return footerStyle === 'boxed' ? (
    <Container
      as="footer"
      wrapper
      padding="px-4 pt-10 pb-6 md:pb-16"
      className={className}
    >
      <Container
        wrapper
        padding="p-6 md:py-10"
        backgroundColor="bg-primary-500"
        className="rounded-custom"
      >
        <div className="text-neutrals-0 grid grid-cols-1 items-start gap-8 md:grid-cols-2 md:py-6 md:text-center xl:grid-cols-4 xl:gap-12 xl:text-left">
          <div className="flex h-full w-full items-center justify-start md:justify-center">
            <Image
              src={image.src}
              alt={image.alt ?? 'Footer Logo'}
              width={image.width}
              height={image.height}
              draggable="true"
              className="md:mx-auto xl:mx-0"
            />
          </div>
          {columns}
        </div>
      </Container>
      <div className="text-primary-500 mt-8 flex flex-col items-center gap-12 text-center">
        <div className="order-2 gap-2 md:order-1">{legal}</div>
        {socialMedia && (
          <div className="text-primary-500 order-1 md:order-2">
            {socialMedia}
          </div>
        )}
      </div>
      {disclaimer && (
        <div className="text-body text-body-xs text-primary-500 mt-6">
          {disclaimer}
        </div>
      )}
    </Container>
  ) : (
    <Container
      as="footer"
      wrapper
      padding="px-4 pt-10 pb-6 md:pb-16"
      backgroundColor="bg-footer-bg"
      className={className}
    >
      <Image
        src={image.src}
        alt={image.alt ?? 'Footer Logo'}
        width={image.width}
        height={image.height}
        draggable="true"
        className="mb-6 md:mx-auto md:mb-8 xl:mx-0"
      />

      <div className="text-footer-text grid grid-cols-1 items-start gap-8 md:grid-cols-2 md:py-6 md:text-center xl:grid-cols-4 xl:gap-12 xl:text-left">
        {columns}
      </div>

      <hr className="border-footer-text/20 my-6" />

      <div className="flex flex-col gap-12 md:items-center md:text-center xl:flex-row xl:justify-between xl:text-left">
        <div className="text-footer-text order-2 gap-2 md:order-1">{legal}</div>
        {socialMedia && (
          <div className="text-footer-text order-1 md:order-2">
            {socialMedia}
          </div>
        )}
      </div>

      {disclaimer && (
        <div className="text-body text-body-xs text-footer-text mt-6">
          {disclaimer}
        </div>
      )}
    </Container>
  );
};

Footer.displayName = 'Footer';
